import { Card } from '@/components/ui/card';

import { MapService } from '@/features/map-locations/model/map.service';
import { TimeMachineControl, UseTimeMachine } from '@/features/map-time-machine';
import { DataLayerSelector } from '@/features/map-data-layers';
import type { CorridorDisplay } from '@/features/map-corridors';

import type { LocationModel } from '@/entities/location';

interface MapContainerProps {
  locations: LocationModel[];
  corridors: CorridorDisplay[];
  selectedLocation: LocationModel | null;
  showCorridors: boolean;
  showTimeMachine: boolean;
  dataLayer: 'stock' | 'releases' | 'requests';
  timeMachine: UseTimeMachine;
  isLoading: boolean;
  loadingMessage?: string;
  onLocationSelect: (location: LocationModel) => void;
  onDataLayerChange: (layer: 'stock' | 'releases' | 'requests') => void;
}

export function MapContainer({
  locations,
  corridors,
  selectedLocation,
  showCorridors,
  showTimeMachine,
  dataLayer,
  timeMachine,
  isLoading,
  loadingMessage,
  onLocationSelect,
  onDataLayerChange
}: MapContainerProps) {
  const mapService = MapService.getInstance();
  const provider = mapService.getProvider();

  if (locations.length === 0) {
    return (
      <Card className="p-4">
        <div className="w-full h-[600px] flex items-center justify-center">
          <p className="text-gray-500">No locations to display</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="w-full h-[600px] relative">
      {isLoading && (
        <div className="absolute top-2 left-2 z-[1000] bg-white rounded px-3 py-2 shadow-md">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
            <span className="text-sm">{loadingMessage || 'Loading...'}</span>
          </div>
        </div>
      )}

      {/* Map */}
      {provider.render({
        locations,
        selectedLocation,
        corridors,
        config: {
          showCorridors
        },
        dataLayer,
        currentWeek: showTimeMachine ? timeMachine.currentWeek : 0,
        onLocationSelect,
        className: 'h-full w-full rounded-lg z-1'
      })}

      <DataLayerSelector activeLayer={dataLayer} onLayerChange={onDataLayerChange} />

      {showTimeMachine && (
        <TimeMachineControl
          currentWeek={timeMachine.currentWeek}
          minWeek={timeMachine.minWeek}
          maxWeek={timeMachine.maxWeek}
          onWeekChange={timeMachine.setWeek}
          onStepBackward={timeMachine.stepBackward}
          onStepForward={timeMachine.stepForward}
          onReset={timeMachine.reset}
        />
      )}
    </div>
  );
}
